'use client'

import { ComponentType, FC, PropsWithChildren } from 'react'
import { AppLayout } from '@/root/modules/tuuleliinid'
import { EditReservationContent } from '@/root/modules/tuuleliinid/EditReservation'
import { BookingContainer } from '@/root/components/tuuleliinid'
import { ReservationWithModification } from '@/root/modules/shared/EditReservation/types'
import { CountryItem, Reservation } from '@/root/shared-types'
import { useGetTranslations, useErrorBanner } from '@/root/shared-hooks'
import { Banner } from '@/root/ui/tuuleliinid/Banner/Banner'
import { SWRConfig } from 'swr'
import { swrConfig } from '@/root/utils/swr/booking-process-config'

export interface EditReservationPageProps {
  /** Last committed state of the reservation — the baseline edits are diffed against. */
  committedReservation: Reservation
  countries: CountryItem[]
  reservation: ReservationWithModification
  /** The page frame. The TL header and footer by default; the trial theme passes its unbranded one. */
  Layout?: ComponentType<PropsWithChildren>
}

interface EditReservationLoadErrorPageProps {
  isReservationNotFound: boolean
  errorCode?: string
  /** The page frame. The TL header and footer by default; the trial theme passes its unbranded one. */
  Layout?: ComponentType<PropsWithChildren>
}

export const EditReservationLoadErrorPage: FC<EditReservationLoadErrorPageProps> = ({
  errorCode,
  isReservationNotFound,
  Layout = AppLayout,
}) => {
  const { getTranslation } = useGetTranslations()
  const message = getTranslation(isReservationNotFound ? 'editReservationLoadNotFound' : 'commonNotificationErrorDesc')

  return (
    <Layout>
      <div className="flex h-full flex-1 flex-col justify-center py-6">
        <BookingContainer>
          <Banner role="alert" variant="error" className="mx-auto w-full max-w-2xl border-0">
            <p>{message}</p>
            {errorCode && <p>{`${getTranslation('errorRedCodePrefix')} ${errorCode}`}</p>}
          </Banner>
        </BookingContainer>
      </div>
    </Layout>
  )
}

export const EditReservationPage: FC<EditReservationPageProps> = ({
  reservation,
  committedReservation,
  countries,
  Layout = AppLayout,
}) => {
  const { getTranslation } = useGetTranslations()
  const { errorBody } = useErrorBanner()

  return (
    <Layout>
      <BookingContainer>
        {errorBody && (
          <Banner title={getTranslation('editErrorSavingTitle')} variant="error" className="my-6 border-0">
            <p className="font-normal whitespace-pre-line">{errorBody}</p>
          </Banner>
        )}

        <SWRConfig value={swrConfig}>
          <EditReservationContent
            reservation={reservation}
            committedReservation={committedReservation}
            countries={countries}
          />
        </SWRConfig>
      </BookingContainer>
    </Layout>
  )
}
